'use client';

import React from 'react';

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Metric {
  label: string;
  value: string;
  icon: string;
}

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  industry: string;
  companySize: string;
  serviceType: string;
  description: string;
  image: string;
  imageAlt: string;
  metrics: Metric[];
  tags: string[];
  testimonial?: {
    quote: string;
    author: string;
    position: string;
    avatar: string;
    avatarAlt: string;
  };
  videoUrl?: string;
  pdfUrl?: string;
}

interface CaseStudyCardProps {
  caseStudy: CaseStudy;
  className?: string;
}

const CaseStudyCard = ({ caseStudy, className = '' }: CaseStudyCardProps) => {
  return (
    <div className={`bg-card rounded-lg shadow-subtle hover:shadow-elevation transition-all duration-300 overflow-hidden ${className}`}>
      {/* Image */}
      <div className="relative h-64 overflow-hidden group">
        <AppImage
          src={caseStudy.image}
          alt={caseStudy.imageAlt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        {caseStudy.videoUrl && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button className="w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow-elevation hover:scale-110 transition-transform duration-300">
              <Icon name="PlayIcon" size={32} className="text-accent-foreground ml-1" />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-accent uppercase tracking-wide">{caseStudy.industry}</span>
            <span className="text-xs text-muted-foreground">{caseStudy.companySize}</span>
          </div>
          <h3 className="text-xl font-display font-bold text-foreground mb-2 line-clamp-2">{caseStudy.title}</h3>
          <p className="text-sm text-muted-foreground mb-3">{caseStudy.client}</p>
          <p className="text-sm text-foreground leading-relaxed line-clamp-3">{caseStudy.description}</p>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-border">
          {caseStudy.metrics.slice(0, 3).map((metric, index) => (
            <div key={index} className="text-center">
              <div className="flex justify-center mb-1">
                <Icon name={metric.icon as any} size={20} className="text-success" />
              </div>
              <div className="text-lg font-display font-bold text-foreground">{metric.value}</div>
              <div className="text-xs text-muted-foreground">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {caseStudy.tags.slice(0, 3).map((tag, index) => (
            <span key={index} className="px-3 py-1 bg-muted text-xs font-medium text-foreground rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CaseStudyCard;